import { Box } from "@mui/material";
import { SubsessionStatus } from "@zoom/videosdk";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { ZoomMediaContext } from "../ZoomMediaProvider";
import SideSheetDrawer from "../components/SidesheetDrawer";
import useDisclosure from "../hooks/use-disclosure";
import ChatContainer from "./chat/chat";
import { useChat } from "./chat/hooks/useChat";
import { Participant } from "./index-types";
import "./subsession.scss";
import { useInviteJoinSubsession } from "./subsession/hooks/useInviteJoinRoom";
import { useSubsession } from "./subsession/hooks/useSubsession";
import { useParticipantsChange } from "./useParticipantsChange";
import { PatientJoinConfirmation } from "./video/components/PatientJoinConfirmation";
import Video from "./video/video";
import VideoAttach from "./video/video-attach";
import VideoSingle from "./video/video-single";
import { ZoomContext } from "./zoom-context";

const JoinRoom: React.FunctionComponent = () => {
  const location = useLocation();
  const zmClient = useContext(ZoomContext);
  if (!zmClient) {
    throw new Error("zmClient is not available. ");
  }
  const subsessionClient = zmClient.getSubsessionClient();
  const mediaContext = useContext(ZoomMediaContext);

  if (!mediaContext) {
    throw new Error("ZoomMediaContext is not available. ");
  }

  const { mediaStream } = mediaContext;
  const [isHostOrManager, setIsHostOrManager] = useState(false);
  const {
    subsessions,
    subsessionStatus,
    openSubsessions,
    assignUserToSubsession,
  } = useSubsession(zmClient, subsessionClient);
  const chatClient = zmClient.getChatClient();
  const {
    records,
    receivers,
    privilege,
    chatUser,
    setChatUserId,
    sendMessage,
    sendFile,
    resendFile,
    downloadFile,
  } = useChat(zmClient, chatClient);
  const { setInviteVisible } = useInviteJoinSubsession(zmClient);
  const [incomingPatient, setIncomingPatient] = useState<Participant[]>([]);
  const openIncomingModalDisc = useDisclosure(false, {
    onClose: () => {
      setInviteVisible(false);
    },
  });
  const ref = useRef(true);
  const count = useRef(0);
  const [hasPatientAttendedCall, setPatientAttendedCall] =
    useState<boolean>(false);
  const disclosureHost = useDisclosure();

  const onParticipantsChange = useCallback(
    (participants: Participant[]) => {
      const normalParticipants = participants?.filter(
        (p) => p.userIdentity !== zmClient?.getCurrentUserInfo()?.userIdentity
      );

      if (
        normalParticipants.length > 0 &&
        count.current === 0 &&
        zmClient?.getCurrentUserInfo()?.isHost
      ) {
        setPatientAttendedCall(true);
        openIncomingModalDisc.open();
        count.current = 1;
      }
      setIncomingPatient(normalParticipants);
      const isHostOrManager = zmClient.isHost() || zmClient.isManager();

      setIsHostOrManager(isHostOrManager);
    },
    [zmClient]
  );

  useParticipantsChange(zmClient, onParticipantsChange);

  // zmClient.on("waiting-room-user-joined", (payload) => {
  //   console.log("val here User in waiting room:", payload);
  // });

  useEffect(() => {
    if (subsessionStatus !== SubsessionStatus.InProgress) {
      setInviteVisible(false);
    }
  }, [subsessionStatus, setInviteVisible]);

  useEffect(() => {
    if (
      ref.current &&
      subsessionClient &&
      zmClient &&
      subsessions?.length > 0
    ) {
      openSubsessions();
      ref.current = false;
    }
  }, [zmClient, subsessionClient, subsessions]);

  const isUseVideoPlayer =
    new URLSearchParams(location.search).get("useVideoPlayer") === "1";

  const handlePatientIncomingConfirmation = () => {
    const content = incomingPatient[0];
    if (subsessions[0]?.subsessionId) {
      assignUserToSubsession(
        content.userId,
        subsessions[0]?.subsessionId,
        content.displayName,
        content.avatar
      );
      subsessionClient?.joinSubsession(subsessions[0]?.subsessionId);

      if (zmClient.getCurrentUserInfo().isHost && mediaStream) {
        // mediaStream.unmuteAudio().then(() => {
        //   console.log("started unmuting");

        //   mediaStream.muteAudio().finally(() => {
        //     console.log("started muting");
        //     setIsLoading(false);
        //     openIncomingModalDisc.close();
        //   });
        // });
        openIncomingModalDisc.close();
      }
    }
  };

  return (
    <Box height={"100%"} width={"100%"}>
      {mediaStream?.isSupportMultipleVideos() ? (
        isUseVideoPlayer ? (
          <VideoAttach
            disclosureHost={disclosureHost}
            hasPatientAttendedCall={hasPatientAttendedCall}
          />
        ) : (
          <Video
            disclosureHost={disclosureHost}
            hasPatientAttendedCall={hasPatientAttendedCall}
          />
        )
      ) : (
        <VideoSingle
          disclosureHost={disclosureHost}
          hasPatientAttendedCall={hasPatientAttendedCall}
        />
      )}

      <SideSheetDrawer
        open={disclosureHost.opened}
        onClose={disclosureHost.close}
        title="In Call Messages"
        w="30vw"
      >
        <ChatContainer
          downloadFile={downloadFile}
          chatUser={chatUser}
          sendFile={sendFile}
          sendMessage={sendMessage}
          resendFile={resendFile}
          receivers={receivers}
          records={records}
          isHostOrManager={isHostOrManager}
          setChatUserId={setChatUserId}
          privilege={privilege}
        />
      </SideSheetDrawer>
      {incomingPatient?.length > 0 && (
        <PatientJoinConfirmation
          disclosureHost={openIncomingModalDisc}
          onConfirm={handlePatientIncomingConfirmation}
          incomingPatient={incomingPatient}
        />
      )}
    </Box>
  );
};

export default JoinRoom;
